import {
    DAY_OF_THE_WEEK,
    EQUAL_TO_OPERATOR,
    eventOperatorOptions,
    RECURRING_IDENTIFIER,
    INVOICE_STATUS_IDENTIFIER,
    DATE_IDENTIFIER,
    OWNER_BLOCK_IDENTIFIER,
    INVOICE_PAYMENT_IDENTIFIER,
    ARRIVAL_DATE_RANGE,
    DEPARTURE_DATE_RANGE,
    AUTOMATED_PAYMENT_IDENTIFIER,
    PAYMENT_REQUEST_IDENTIFIER,
    DAMAGE_NOTICE_IDENTIFIER,
    WORK_ORDER_IDENTIFIER,
    WORK_ORDER_SOURCE_IDENTIFIER,
    GUEST_STATUS_IDENTIFIER
} from './constants';
import {
    RECURRENCE_FREQ_YEARLY,
    RECURRENCE_FREQ_MONTHLY,
    RECURRENCE_FREQ_WEEKLY,
    RECURRENCE_FREQ_DAILY,
    RECURRENCE_MONTH_MAPPING,
    RECURRENCE_WEEKDAY_MAPPING,
} from '../../../../constants';
import range from 'lodash/range';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';

import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import { getDaysInMonth } from '../../../../utils/dates';

export const getEventIsDepartureOrArrival = (event) => {
    return ['DEPARTURE_DATE_PAST', 'DEPARTURE_DATE_FUTURE', 'ARRIVAL_DATE_PAST', 'ARRIVAL_DATE_FUTURE'].includes(
        event.source
    );
};

export const prepareConditionToSend = (condition) => {
    if (condition.source === DAY_OF_THE_WEEK) {
        return {
            uuid: condition.uuid,
            identifier: condition.source,
            day: condition.target,
        };
    }
    if (condition.source === ARRIVAL_DATE_RANGE || condition.source === DEPARTURE_DATE_RANGE) {
        return {
            uuid: condition.uuid,
            identifier: condition.source,
            from_month: condition.from_month,
            from_day: condition.from_day,
            to_month: condition.to_month,
            to_day: condition.to_day,
        };
    }
    if (condition.source === OWNER_BLOCK_IDENTIFIER) {
        return {
            uuid: condition.uuid,
            identifier: condition.source,
            deleted_by: condition.target,
        };
    }
    if (condition.source === WORK_ORDER_SOURCE_IDENTIFIER) {
        return {
            uuid: condition.uuid,
            identifier: condition.source,
            source: condition.target,
        };
    }
    if(condition.source === GUEST_STATUS_IDENTIFIER) {
        return {
            uuid: condition.uuid,
            identifier: condition.source,
            status: condition.target,
            operator: condition.operator,
        };
    }
    return {
        uuid: condition.uuid,
        identifier: condition.source,
        status: condition.target,
    };
};

export const prepareEventToSend = (event) => {
    if (event.source === DAY_OF_THE_WEEK) {
        return {
            uuid: event.uuid,
            identifier: event.source,
            day: event.target,
        };
    }
    if (event.source === RECURRING_IDENTIFIER) {
        return {
            uuid: event.uuid,
            identifier: event.source,
            recurrence: {
                freq: event.freq,
                interval: event.interval,
                byday: event.byday,
                bymonth: [event.bymonth].filter((i) => !!i),
                bymonthday: event.bymonthday.filter((i) => !!i),
                start: event.start,
                until: event.until || null,
            },
            recurrence_id: event.recurrence_id,
        };
    }
    if (event.source === INVOICE_STATUS_IDENTIFIER) {
        return {
            uuid: event.uuid,
            identifier: event.source,
            status: event.target,
            action: event.operator,
        };
    }
    if (event.source === DATE_IDENTIFIER) {
        return {
            uuid: event.uuid,
            identifier: event.source,
            date: event.date,
        };
    }
    if (getEventIsDepartureOrArrival(event)) {
        const range = event.target.split('_');
        return {
            uuid: event.uuid,
            identifier: event.source,
            range_value: range.length > 0 ? Number(range[0]) : 0,
            range_unit: range.length > 1 ? range[1] : null,
        };
    }
    if (
        event.source === OWNER_BLOCK_IDENTIFIER ||
        event.source === INVOICE_PAYMENT_IDENTIFIER ||
        event.source === AUTOMATED_PAYMENT_IDENTIFIER ||
        event.source === PAYMENT_REQUEST_IDENTIFIER ||
        event.source === DAMAGE_NOTICE_IDENTIFIER ||
        event.source === WORK_ORDER_IDENTIFIER
    ) {
        return {
            uuid: event.uuid,
            identifier: event.source,
            action: event.target,
        };
    }
    return {
        uuid: event.uuid,
        identifier: event.source,
        status: event.target,
    };
};

export const getPreparedConditionObj = (condition) => {
    let selectedTarget = '';
    const additionalProps = {};
    if (condition.day !== undefined) {
        selectedTarget = condition.day;
    }
    if (condition.identifier === ARRIVAL_DATE_RANGE || condition.identifier === DEPARTURE_DATE_RANGE) {
        additionalProps.from_month = condition.from_month;
        additionalProps.from_day = condition.from_day;
        additionalProps.to_month = condition.to_month;
        additionalProps.to_day = condition.to_day;
    }
    if (condition.identifier === OWNER_BLOCK_IDENTIFIER) {
        selectedTarget = condition.deleted_by;
    }
    if (condition.identifier === WORK_ORDER_SOURCE_IDENTIFIER) {
        selectedTarget = condition.source;
    }

    return {
        uuid: condition.uuid,
        source: condition.identifier,
        operator: condition.operator || EQUAL_TO_OPERATOR.value,
        target: condition.status !== undefined ? condition.status : selectedTarget,
        ...additionalProps,
    };
};

export const getPreparedEventObj = (event, isCopy) => {
    let selectedTarget = '';
    if (event.day !== undefined) {
        selectedTarget = event.day;
    }
    if (event.range_value !== undefined) {
        selectedTarget = `${event.range_value}_${event.range_unit || 'HOUR'}`;
    }
    if (event.action !== undefined) {
        selectedTarget = event.action;
    }
    const recurrence = event.recurrence || {};
    if (!isEmpty(recurrence)) {
        recurrence.bymonth = recurrence.bymonth.length > 0 ? recurrence.bymonth[0] : null;
        recurrence.bymonthday = recurrence.bymonthday.length > 0 ? recurrence.bymonthday : [];
        if (isCopy) {
            delete recurrence.id;
        } else {
            recurrence.recurrence_id = event?.recurrence?.id;
        }
    }
    let operator = '';

    if (eventOperatorOptions[event.identifier]) {
        if (isArray(eventOperatorOptions[event.identifier]) && eventOperatorOptions[event.identifier].length > 0) {
            operator = eventOperatorOptions[event.identifier][0].value;
        } else {
            operator = eventOperatorOptions[event.identifier].value;
        }
    }

    if (event.identifier === INVOICE_STATUS_IDENTIFIER) {
        operator = event.action;
    }
    if (event.identifier === 'RENTAL_AGREEMENT') {
        selectedTarget = 'SIGNED';
    }
    if (event.identifier === 'STAYLIO_GUEST_APP') {
        selectedTarget = 'INSTALLED';
    }

    return {
        uuid: event.uuid,
        source: event.identifier,
        operator,
        target: event.status !== undefined ? event.status : selectedTarget,
        date: event.date,
        ...recurrence,
    };
};

const getEventGroup = (condition, eventTypes) => {
    let group = '';
    Object.keys(eventTypes).forEach((event) => {
        if (eventTypes[event].includes(condition.value)) {
            group = event;
        }
    });
    return group;
};

export const prepareConditionIdentifiers = (conditionIdentifiers, eventTypes) => {
    return conditionIdentifiers.map((item) => {
        const group = getEventGroup(item, eventTypes);

        if (group) {
            return {
                ...item,
                group: group === 'TIME' ? 'Time Based Condition' : 'System Condition',
            };
        }
        return item;
    });
};

export const prepareEventIdentifiers = (eventIdentifiers, eventTypes) => {
    return eventIdentifiers.map((item) => {
        const group = getEventGroup(item, eventTypes);
        if (group) {
            return {
                ...item,
                group: group === 'TIME' ? 'Time Based Event' : group === 'CHANNEL' ? 'Channel Event' : 'System Event',
                text: item.text,
            };
        }
        return item;
    });
};

export const getFreqOptions = (interval) => {
    const isPlural = !!(interval > 1);

    return [
        {
            value: RECURRENCE_FREQ_DAILY,
            text: isPlural ? 'days' : 'day',
        },
        {
            value: RECURRENCE_FREQ_WEEKLY,
            text: isPlural ? 'weeks' : 'week',
        },
        {
            value: RECURRENCE_FREQ_MONTHLY,
            text: isPlural ? 'months' : 'month',
        },
        {
            value: RECURRENCE_FREQ_YEARLY,
            text: isPlural ? 'years' : 'year',
        },
    ];
};

export const bymonthDays = (bymonth) => {
    // We use year: 1000 since we want to ignore leap years
    return getDaysInMonth({ year: 1000, month: bymonth });
};
export const yearlyMonthDayOptions = (bymonth) => {
    return range(1, bymonthDays(bymonth) + 1).map((n) => ({
        value: n,
        text: n.toString(),
    }));
};
export const monthDayOptions = () => {
    return range(1, 32)
        .map((n) => ({ value: n, text: n.toString() }))
        .concat([{ value: -1, text: 'Last day of the month' }]);
};
export const monthOptions = () => {
    return Object.entries(RECURRENCE_MONTH_MAPPING).map(([month, monthText]) => ({
        value: Number(month),
        text: monthText,
    }));
};
export const weekdayOptions = () => {
    return Object.entries(RECURRENCE_WEEKDAY_MAPPING).map(([weekday, weekdayText]) => ({
        value: weekday,
        text: weekdayText,
    }));
};

export const generateRecurringString = (recurrence, userDateFormat) => {
    let date = '';
    const monthDayLabels = recurrence.bymonthday.map((item) => {
        if (item === -1) {
            return 'Last day of the month';
        }
        return item;
    });
    if (recurrence.start) {
        date = ` from ${format(parseISO(recurrence.start), userDateFormat)} ${recurrence.until ? 'until' : ''} ${
            recurrence.until ? format(parseISO(recurrence.until), userDateFormat) : ''
        }`;
    }
    let repeat = '';
    if (recurrence.freq === 'DAILY') {
        repeat = `repeats every ${recurrence.interval || ''} day`;
    }
    if (recurrence.freq === 'MONTHLY') {
        repeat = `repeats every ${recurrence.interval || ''} month on ${monthDayLabels.join(',')} day`;
    }
    if (recurrence.freq === 'WEEKLY') {
        repeat = `repeats every ${recurrence.interval} week on ${recurrence.byday.join(',')}`;
    }
    if (recurrence.freq === 'YEARLY') {
        repeat = `repeats every ${recurrence.interval || ''} year on ${recurrence.bymonth ||
            ''} month on ${recurrence.bymonthday.join(',')} day`;
    }

    return repeat + date;
};

export const generateDateRangeString = (condition) => {
    const startMonth = monthOptions().find((item) => item.value === condition.from_month)?.text;
    const endMonth = monthOptions().find((item) => item.value === condition.to_month)?.text;
    return `from ${startMonth || ''} ${condition.from_day || ''} to ${endMonth || ''} ${condition.to_day || ''}`;
};

export const getIsRecurringEvent = (source) => {
    return source === 'RECURRING';
};

export const getIsDateEvent = (source) => {
    return source === 'DATE';
};

export const prepareActionToSend = (action) => {
    if (action.type === 'SMS') {
        return {
            message: action.sms,
        };
    }
    if (action.type === 'PUSH') {
        return {
            message: action.message,
            subject: action.subject,
        };
    }

    const payload = {
        uploaded_file: action.selectedUploadedFile,
        report_template: action.selectedReportTemplate,
        report_settings: action.selectedReportSettings,
        calendar_range: action.selectedCalendarRange,
        message: action.email,
        subject: action.subject,
        email_receivers: action.recipients,
        unlayer_template: action.selectedUnlayerPdfTemplate,
    };
    if (action.selectedUploadedFile === -1) {
        payload.property_file = true;
        payload.uploaded_file = null;
    }
    return payload;
};

export const checkInOption = {
    text: 'At Check-in',
    value: '0_HOUR',
};

const generateDayEventOption = () => {
    return Array.from({ length: 75 }, (v, k) => {
        return {
            text: `${k + 1} ${k === 0 ? 'day' : 'days'}`,
            value: `${k + 1}_DAY`,
        };
    });
};

export const triggerTimeEventOptions = [
    {
        text: '1 Hour',
        value: '1_HOUR',
    },
    {
        text: '2 Hours',
        value: '2_HOUR',
    },
    {
        text: '6 Hours',
        value: '6_HOUR',
    },
    {
        text: '12 Hours',
        value: '12_HOUR',
    },
    ...generateDayEventOption(),
    {
        text: '90 days',
        value: '90_DAY',
    },
    {
        text: '120 days',
        value: '120_DAY',
    },
    {
        text: '180 days',
        value: '180_DAY',
    },
];

export const calculateSmsLength = (sms, tagsLengthObj) => {
    let text = sms;
    if (tagsLengthObj) {
        Object.keys(tagsLengthObj).forEach((item) => {
            if (text.indexOf(item) !== -1) {
                text = text.replaceAll(`[${item}]`, 'x'.repeat(tagsLengthObj[item]));
            }
        });
    }
    return text.length;
};
